/**
 * Typed fetch wrappers for the /admin/ai/* endpoints (SYR-33 Phase 1 backend).
 *
 * Centralises JWT header handling and gives the AI admin hub UI typed access
 * to the versioning / snapshots / shared-prompt-library endpoints without
 * adding 30+ entries to the existing nestjs-base-api monolith.
 *
 * Phase 3 cutovers can migrate individual subsystems' Configure tabs to RTK
 * Query if real-time cache invalidation matters; until then plain fetch is
 * simpler.
 */

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL ?? '';

type JsonShape = Record<string, unknown>;

class AiAdminApiError extends Error {
    constructor(public readonly status: number, message: string) {
        super(message);
    }
}

async function request<T>(
    path: string,
    jwtToken: string,
    init: { method?: string; body?: JsonShape; query?: Record<string, string | number | undefined> } = {},
): Promise<T> {
    if (!jwtToken) throw new AiAdminApiError(401, 'Not authenticated');

    let url = `${apiBaseUrl}admin/ai${path}`;
    if (init.query) {
        const sp = new URLSearchParams();
        for (const [k, v] of Object.entries(init.query)) {
            if (v !== undefined) sp.set(k, String(v));
        }
        const qs = sp.toString();
        if (qs) url += `?${qs}`;
    }

    const response = await fetch(url, {
        method: init.method ?? 'GET',
        headers: {
            Authorization: `Bearer ${jwtToken}`,
            ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        },
        body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    });

    if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        throw new AiAdminApiError(
            response.status,
            errorBody?.message || `Request failed (${response.status})`,
        );
    }
    if (response.status === 204) return undefined as unknown as T;
    return (await response.json()) as T;
}

// ----- Types -----------------------------------------------------------

export type SubsystemId =
    | 'ai_experts'
    | 'advice_ai'
    | 'orbital_ai'
    | 'building_blocks_ai'
    | 'briefing_ai'
    | 'hypothesis_ai'
    | 'model_beliefs_ai'
    | 'auto_concierge_ai'
    | 'element_analysis'
    | 'response_monitoring_ai'
    | 'editor_ai'
    | 'token_quotas'
    | 'knowledge_base'
    | 'models_providers'
    | 'shared_prompt_library';

export type SubsystemGroup =
    | 'brand_insight'
    | 'conversational'
    | 'operational'
    | 'shared_prompt_library'
    | 'infrastructure';

export type SubsystemVersionStatus =
    | 'draft'
    | 'published'
    | 'superseded'
    | 'rolled_back'
    | 'discarded';

export interface SubsystemSummary {
    id: SubsystemId;
    displayName: string;
    subtitle: string;
    group: SubsystemGroup;
    dependencies: SubsystemId[];
    currentPublishedVersion: number | null;
    currentPublishedVersionId: number | null;
    hasDraft: boolean;
    draftVersionId: number | null;
    lastPublishedAt: string | null;
}

export interface SubsystemDetail extends SubsystemSummary {
    publishedPayload: unknown | null;
    draftPayload: unknown | null;
    // Live state, read fresh — NOT the last published payload. Seeding an
    // editor from `publishedPayload` means any change made outside the Hub is
    // reverted by the next publish, silently, because nothing looked at live.
    livePayload: unknown | null;
}

/**
 * The system settings actually in force for a subsystem.
 *
 * NOT `publishedPayload`: a subsystem seeded or refreshed by SQL and never
 * published through the Hub has none, which is the normal state on dev. Reading
 * it makes a live catalogue look empty — every model in the Model Beliefs panel
 * rendered as "not in catalogue" while runs were calling those models happily.
 */
export function liveSettingsKeys(
    detail: SubsystemDetail | null | undefined,
): Record<string, string> {
    const payload = (detail?.livePayload ??
        detail?.publishedPayload ??
        detail?.draftPayload) as { systemSettingsKeys?: Record<string, string> } | null;
    return payload?.systemSettingsKeys ?? {};
}

export interface SubsystemVersion {
    id: number;
    subsystem: string;
    versionNumber: number;
    status: SubsystemVersionStatus;
    payloadJson: unknown;
    parentVersionId: number | null;
    notes: string | null;
    createdBy: number;
    createdAt: string;
    publishedAt: string | null;
    supersededAt: string | null;
}

export interface SystemSettingDiff {
    name: string;
    before: string | null;
    after: string | null;
}

export interface ConfigTableRowDiff {
    rowKey: string;
    kind: 'added' | 'removed' | 'changed';
    before?: Record<string, unknown> | null;
    after?: Record<string, unknown> | null;
    changedFields?: string[];
}

export interface SubsystemDiff {
    subsystem: string;
    fromVersion: number;
    toVersion: number;
    systemSettings: SystemSettingDiff[];
    configTableRows: Record<string, ConfigTableRowDiff[]>;
    hasChanges: boolean;
}

export interface GlobalSnapshot {
    id: number;
    name: string;
    notes: string | null;
    versionPointersJson: Record<string, number>;
    createdBy: number;
    createdAt: string;
}

export interface SharedPromptLibraryEntry {
    settingKey: string;
    displayName: string | null;
    description: string | null;
    isConfidential: boolean;
    allowedSubsystems: string[];
    value?: string | null;
    usedBy?: string[];
}

export interface SandboxScenario {
    id: number;
    subsystem: SubsystemId;
    name: string;
    description: string | null;
    brandId: number | null;
    brandName?: string | null;
    periodId: number | null;
    hasFrozenInputs: boolean;
    isCurated: boolean;
    createdBy: number;
    creatorName?: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface SandboxScenarioDetail extends SandboxScenario {
    inputsJson: unknown | null;
}

export type SandboxRunStatus = 'queued' | 'running' | 'complete' | 'error';

export interface SandboxRunOutput {
    side: 'a' | 'b';
    resolvedSystemPrompt: string | null;
    resolvedUserPrompt: string | null;
    outputJson: unknown | null;
    inputTokens: number | null;
    outputTokens: number | null;
    costUsd: number | null;
    latencyMs: number | null;
    modelId: string | null;
    provider: string | null;
    temperature: number | null;
}

export interface SandboxRun {
    id: number;
    subsystem: SubsystemId;
    scenarioId: number | null;
    scenarioName?: string | null;
    brandId: number | null;
    brandName?: string | null;
    periodId: number | null;
    inputsJson: unknown | null;
    configAVersionId: number;
    configAVersionNumber?: number | null;
    configBVersionId: number;
    configBVersionNumber?: number | null;
    status: SandboxRunStatus;
    errorMessage: string | null;
    estimatedCostUsd: number | null;
    actualCostUsd: number | null;
    createdBy: number;
    createdByName?: string | null;
    createdAt: string;
    completedAt: string | null;
}

export interface SandboxRunDetail extends SandboxRun {
    outputs: SandboxRunOutput[];
}

// ----- The Hypothesis (SYR-90) ----------------------------------------

export interface HypothesisJobRow {
    id: number;
    /** Phase 7 — the research subject; org/brand are null for prospects. */
    subjectId: number;
    organisationId: number | null;
    brandId: number | null;
    status:
        | 'QUEUED_RESEARCH'
        | 'RESEARCHING'
        | 'RESEARCH_DONE'
        // The crawl worked but found nowhere this brand's customers speak.
        // Waiting on an admin to supply review pages, then Continue.
        | 'AWAITING_REVIEWS'
        | 'FORMING'
        | 'READY'
        | 'INSUFFICIENT_DATA'
        | 'FAILED'
        | 'SKIPPED';
    websiteUrl: string | null;
    activatedAt: string | null;
    // Resolved server-side so the table shows names rather than ids. brandName
    // falls back to the SUBJECT's name for prospects, which have no brand row.
    organisationName?: string | null;
    brandName?: string | null;
    subjectName?: string | null;
    // Leads riding on this job's SUBJECT, newest first. Empty for admin
    // prospects and Auto-Concierge runs, which have no request behind them —
    // which is why the lead-level actions are conditional rather than assumed.
    leads?: Array<{
        id: number;
        email: string;
        status: string;
        reasonCode: string | null;
        resultEmailSentAt: string | null;
        crmCompanyId: number | null;
    }>;
    /**
     * False on an Auto-Concierge job: it wanted the crawl, not a read-out.
     * BOTH formation lanes filter on this, so a job sitting at RESEARCH_DONE
     * with it false will never produce a hypothesis and nothing reclaims it.
     * Surfaced in the table because that state used to be invisible.
     */
    formHypothesis: boolean;
    adminGuidance?: string | null;
    researchStartedAt: string | null;
    researchCompletedAt: string | null;
    evidenceJson: {
        websiteReached?: boolean;
        pagesFetched?: Array<{ url: string; chars: number }>;
        place?: {
            found: boolean;
            name?: string;
            rating?: number;
            totalReviews?: number;
            listingCount?: number;
            aggregateRating?: number;
            aggregateReviewCount?: number;
            sampledLocations?: Array<{
                placeId: string;
                name: string;
                reviewsCollected: number;
            }>;
        };
        reviewTextsCollected?: number;
        reviewSource?: string;
        ecosystemSources?: Array<{ source: string; url: string; chars: number }>;
        competitors?: Array<{
            name: string;
            website?: string;
            rating?: number;
            totalReviews?: number;
            pagesFetched: number;
        }>;
        competitiveLandscape?: string;
        evidenceNotes?: string;
    } | null;
    evidenceScore: number | null;
    attemptCount: number;
    lastError: string | null;
    createdAt: string;
    updatedAt: string;
}

export type HypothesisSignal =
    | 'STRENGTH'
    | 'WATCH'
    | 'LIKELY_CONSTRAINT'
    | 'INSUFFICIENT_SIGNAL';

export interface HypothesisElementReading {
    /** Display name (server-side toDisplayName applied). */
    element: string;
    signal: HypothesisSignal;
    note: string;
}

export interface HypothesisAdminSubject {
    id: number;
    subjectType: HypothesisSubjectType;
    name: string;
    websiteUrl: string | null;
    organisationId: number | null;
    brandId: number | null;
    crmCompanyId: number | null;
    researchedAt: string | null;
}

export interface HypothesisAdminDetail {
    subject: HypothesisAdminSubject | null;
    job: HypothesisJobRow | null;
    hypothesis: {
        id: number;
        status: 'ACTIVE' | 'CONFIRMED' | 'OVERTURNED' | 'EXPIRED';
        headline: string;
        claim: string;
        closingQuestion: string;
        sourcesSummary: string;
        constraintElement: string;
        darkHorseElement: string | null;
        elementReadings: HypothesisElementReading[];
        evidenceBullets: string[];
        competitorsAnalysed: string[];
        dismissedAt: string | null;
        teaseEmailSentAt: string | null;
        revealEmailSentAt: string | null;
        resolvedAt: string | null;
        createdAt: string;
        updatedAt: string;
    } | null;
}

export interface HypothesisTeasePreview {
    subject: string;
    bodyHtml: string;
}

export type HypothesisSubjectType = 'PLATFORM' | 'PROSPECT';

/** SYR-90 Phase 7 — a researchable thing: a platform brand or a prospect. */
export interface HypothesisSubjectRow {
    id: number;
    subjectType: HypothesisSubjectType;
    name: string;
    websiteUrl: string | null;
    organisationId: number | null;
    brandId: number | null;
    crmCompanyId: number | null;
    researchedAt: string | null;
    createdAt: string;
    jobStatus: HypothesisJobRow['status'] | null;
    constraintElement: string | null;
    teaseEmailSentAt: string | null;
}

/**
 * SYR-107 — how often each element is picked as the constraint.
 *
 * `unusedElements` is the sharper signal: one element dominating can be real,
 * but an element the hypothesis is allowed to choose and never does means the
 * public evidence cannot speak to it at all.
 */
export interface HypothesisConstraintDistribution {
    total: number;
    skewed: boolean;
    threshold: number;
    unusedElements: string[];
    breakdown: Array<{ element: string; count: number; share: number }>;
}

// ----- Subsystem endpoints --------------------------------------------

export const aiAdminApi = {
    listSubsystems: (jwt: string) =>
        request<SubsystemSummary[]>('/subsystems', jwt),

    // Model Beliefs (SYR-111). Runs are the unit anyone asks about, so the
    // list carries enough to judge one without opening it, and cost is its own
    // call because it joins token rows the runs table knows nothing about.
    listModelBeliefsRuns: (
        jwt: string,
        params: { brandId?: number; status?: string; limit?: number } = {},
    ) =>
        request<ModelBeliefsRunsResponse>('/model-beliefs/runs', jwt, {
            query: {
                brandId: params.brandId,
                status: params.status,
                limit: params.limit,
            },
        }),

    getModelBeliefsRun: (jwt: string, runId: number) =>
        request<ModelBeliefsRunDetail>(`/model-beliefs/runs/${runId}`, jwt),

    getModelBeliefsRunCost: (jwt: string, runId: number) =>
        request<ModelBeliefsRunCost>(`/model-beliefs/runs/${runId}/cost`, jwt),

    // The gate as measured, plus the raw listings. The listings are where a
    // matching miss shows up: a brand the models named that we failed to
    // recognise looks exactly like a brand they never named.
    getModelBeliefsGate: (jwt: string, runId: number, rows = false) =>
        request<ModelBeliefsGate>(`/model-beliefs/runs/${runId}/gate`, jwt, {
            query: { rows: rows ? 'true' : undefined },
        }),

    getModelBeliefsSources: (jwt: string, runId: number) =>
        request<ModelBeliefsSources>(`/model-beliefs/runs/${runId}/sources`, jwt),

    // v4: the pages the rank question read, with fetch and read status.
    getModelBeliefsPages: (jwt: string, runId: number) =>
        request<ModelBeliefsPagesResponse>(`/model-beliefs/runs/${runId}/pages`, jwt),

    getModelBeliefsResults: (jwt: string, runId: number) =>
        request<ModelBeliefsResultRow[]>(
            `/model-beliefs/runs/${runId}/results`,
            jwt,
        ),

    getModelBeliefsSynthesis: (jwt: string, runId: number) =>
        request<{ runId: number; synthesis: ModelBeliefsSynthesis | null }>(
            `/model-beliefs/runs/${runId}/synthesis`,
            jwt,
        ),

    // Writes the read again from the stored run: a new synthesis call with the
    // reviewer pass, on the brand's CURRENT config (price positioning). SPENDS
    // about forty cents.
    synthesiseModelBeliefsRun: (jwt: string, runId: number) =>
        request<{ runId: number; inputChars: number; synthesis: ModelBeliefsSynthesis | null }>(
            `/model-beliefs/runs/${runId}/synthesise`,
            jwt,
            { method: 'POST', body: {} },
        ),

    // Rebuilds the gate summary from the stored answers: rank scores for every
    // brand the models named, configured or not. Free, no model is called.
    resummariseModelBeliefsGate: (jwt: string, runId: number) =>
        request<{ runId: number; resummarised: boolean }>(
            `/model-beliefs/runs/${runId}/resummarise-gate`,
            jwt,
            { method: 'POST', body: {} },
        ),

    // Reads the pages of a finished run again in the background: recollects
    // them, fetches what is new, reads what is unread, re-aggregates, and
    // re-writes the read. With retryUnreadable the pages that could not be
    // fetched last time are tried again. SPENDS MONEY (about six calls a
    // page unread) and Firecrawl credits.
    readModelBeliefsPages: (jwt: string, runId: number, retryUnreadable: boolean) =>
        request<{ runId: number; started: boolean; alreadyRunning: boolean; retried: number }>(
            `/model-beliefs/runs/${runId}/read-pages`,
            jwt,
            { method: 'POST', body: { retryUnreadable } },
        ),

    getModelBeliefsPanel: (jwt: string) =>
        request<ModelBeliefsPanel>('/model-beliefs/panel', jwt),

    // Brand config. The list is configured-brands-first because the question
    // is "what is set up to be read", not "what brands exist".
    listModelBeliefsBrands: (
        jwt: string,
        params: { search?: string; configuredOnly?: boolean; limit?: number } = {},
    ) =>
        request<ModelBeliefsBrandsResponse>('/model-beliefs/brands', jwt, {
            query: {
                search: params.search || undefined,
                configuredOnly: params.configuredOnly ? 'true' : undefined,
                limit: params.limit,
            },
        }),

    // The brand's reads over time. Its own series on its own surface: the AI
    // read is deliberately NOT drawn on the survey Trends chart, because the
    // two share a scale without sharing a calibration or a clock.
    // The customer read for one run, assembled by the customer assembler. The
    // preview renders the same component the customer page does, so it cannot
    // drift from what a customer sees.
    // The instrument for a template type: what is asked, in which voice, and
    // how many paraphrases each statement carries.
    listModelBeliefsStatements: (jwt: string, templateType: string) =>
        request<ModelBeliefsStatementsResponse>('/model-beliefs/statements', jwt, {
            query: { templateType },
        }),

    // Voice and no-referent are what a statement MEANS to the instrument, so
    // the save returns warnings rather than silently accepting a combination
    // that would measure the wrong thing.
    saveModelBeliefsStatementMeta: (
        jwt: string,
        body: {
            templateType: string;
            code: string;
            voice?: 'subject' | 'witness' | 'fact';
            noReferent?: boolean;
            group?: ModelBeliefsGroup | null;
            wordings?: { id: string; text: string }[] | null;
        },
    ) =>
        request<{
            code: string;
            voice: string;
            noReferent: boolean;
            group: ModelBeliefsGroup | null;
            wordings: { id: string; text: string }[];
            warnings: string[];
        }>('/model-beliefs/statements', jwt, { method: 'PUT', body: body as any }),

    getModelBeliefsRunRead: (jwt: string, runId: number) =>
        request<{
            runId: number;
            brandId: number | null;
            adHoc: boolean;
            status: string;
            latest: any;
            history: unknown[];
        }>(`/model-beliefs/runs/${runId}/read`, jwt),

    getModelBeliefsBrandHistory: (jwt: string, brandId: number) =>
        request<ModelBeliefsBrandHistory>(
            `/model-beliefs/brands/${brandId}/history`,
            jwt,
        ),

    getModelBeliefsBrandConfig: (jwt: string, brandId: number) =>
        request<ModelBeliefsBrandConfigResponse>(
            `/model-beliefs/brands/${brandId}/config`,
            jwt,
        ),

    // Configs by id: an ad-hoc target (no brand) or a brand's, same editor.
    listModelBeliefsAdhocConfigs: (jwt: string) =>
        request<{ configs: ModelBeliefsAdhocConfigRow[]; total: number }>(
            '/model-beliefs/configs',
            jwt,
            { query: { adhoc: 'true' } },
        ),

    getModelBeliefsConfig: (jwt: string, configId: number) =>
        request<ModelBeliefsBrandConfigResponse>(`/model-beliefs/configs/${configId}`, jwt),

    putModelBeliefsConfig: (jwt: string, configId: number, patch: ModelBeliefsBrandConfigPatch) =>
        request<ModelBeliefsBrandConfigResponse>(`/model-beliefs/configs/${configId}`, jwt, {
            method: 'PUT',
            body: patch as unknown as JsonShape,
        }),

    suggestModelBeliefsConfigCategories: (jwt: string, configId: number) =>
        request<ModelBeliefsCategorySuggestions>(
            `/model-beliefs/configs/${configId}/suggest-categories`,
            jwt,
            { method: 'POST' },
        ),

    suggestModelBeliefsConfigCompetitors: (jwt: string, configId: number) =>
        request<ModelBeliefsCompetitorSuggestions>(
            `/model-beliefs/configs/${configId}/suggest-competitors`,
            jwt,
            { method: 'POST' },
        ),

    // An ad-hoc run from before ad-hoc targets had configs: make its config
    // from the stored target so it can be edited and written again.
    adhocConfigForModelBeliefsRun: (jwt: string, runId: number) =>
        request<{ runId: number; configId: number; config: ModelBeliefsBrandConfig }>(
            `/model-beliefs/runs/${runId}/adhoc-config`,
            jwt,
            { method: 'POST' },
        ),

    // The save returns the warnings and the remaining suggestions, so the
    // editor never has to recompute either or re-fetch to show them.
    putModelBeliefsBrandConfig: (
        jwt: string,
        brandId: number,
        patch: ModelBeliefsBrandConfigPatch,
    ) =>
        request<ModelBeliefsBrandConfigResponse>(
            `/model-beliefs/brands/${brandId}/config`,
            jwt,
            { method: 'PUT', body: patch as unknown as JsonShape },
        ),

    // v4 phase 0: suggest the rank question's category from the brand's own
    // site. Spends one searched call on the read's model.
    suggestModelBeliefsCategories: (jwt: string, brandId: number) =>
        request<ModelBeliefsCategorySuggestions>(
            `/model-beliefs/brands/${brandId}/config/suggest-categories`,
            jwt,
            { method: 'POST' },
        ),

    cancelModelBeliefsRun: (jwt: string, runId: number) =>
        request<{ runId: number; cancelled: boolean }>(
            `/model-beliefs/runs/${runId}/cancel`,
            jwt,
            { method: 'POST' },
        ),

    // Competitors for a brand no run has named any for: one searched call,
    // not stored, shown as chips to pick from.
    suggestModelBeliefsCompetitors: (jwt: string, brandId: number) =>
        request<ModelBeliefsCompetitorSuggestions>(
            `/model-beliefs/brands/${brandId}/config/suggest-competitors`,
            jwt,
            { method: 'POST' },
        ),

    // Start a read of a platform brand, or price one with `dryRun: true`.
    // The dry run returns the plan; a real trigger returns the queued run.
    triggerModelBeliefsRun: (
        jwt: string,
        brandId: number,
        overrides: ModelBeliefsTriggerOverrides,
    ) =>
        request<ModelBeliefsTriggerResponse>(
            `/model-beliefs/brands/${brandId}/trigger`,
            jwt,
            { method: 'POST', body: overrides as unknown as JsonShape },
        ),

    // A brand that is not on the platform: the whole config travels in the
    // body. Same dry-run contract as the brand trigger.
    triggerModelBeliefsAdhocRun: (
        jwt: string,
        target: ModelBeliefsAdhocTarget,
        overrides: ModelBeliefsTriggerOverrides,
    ) =>
        request<ModelBeliefsTriggerResponse>('/model-beliefs/adhoc/trigger', jwt, {
            method: 'POST',
            body: { target, overrides } as unknown as JsonShape,
        }),

    suggestModelBeliefsAdhocCategories: (
        jwt: string,
        body: { websiteUrl: string; name: string; country: string },
    ) =>
        request<ModelBeliefsCategorySuggestions>(
            '/model-beliefs/adhoc/suggest-categories',
            jwt,
            { method: 'POST', body },
        ),

    suggestModelBeliefsAdhocCompetitors: (
        jwt: string,
        body: { websiteUrl: string; name: string; country: string; category: string },
    ) =>
        request<ModelBeliefsCompetitorSuggestions>(
            '/model-beliefs/adhoc/suggest-competitors',
            jwt,
            { method: 'POST', body },
        ),

    // An ad-hoc read becomes the brand's first read.
    attachModelBeliefsRun: (jwt: string, runId: number, brandId: number) =>
        request<{
            runId: number;
            brandId: number;
            organisationId: number;
            triggerSource: string;
            configCreated: boolean;
        }>(`/model-beliefs/runs/${runId}/attach`, jwt, {
            method: 'POST',
            body: { brandId },
        }),

    // The Hypothesis (SYR-107) — constraint-pick distribution, so a structural
    // bias surfaces without anyone having to notice it read-out by read-out.
    getHypothesisConstraintDistribution: (jwt: string, limit?: number) =>
        request<HypothesisConstraintDistribution>(
            '/hypothesis/constraint-distribution',
            jwt,
            { query: { limit: limit ? String(limit) : undefined } },
        ),

    // The Hypothesis (SYR-90) — research job visibility + control
    // Where the run came from, which is not the same as who triggered it:
    //   signup       a brand on the platform — the day-0 run that draws them
    //                back in with a tease email and a card on their Briefing
    //   prospect     a brand NOT on the platform, researched by an admin
    //   public_tool  a request through brandscience.ai/hypothesis
    listHypothesisJobs: (
        jwt: string,
        status?: string,
        origin?: 'admin' | 'signup' | 'prospect' | 'public_tool' | 'all',
    ) =>
        request<HypothesisJobRow[]>('/hypothesis/jobs', jwt, {
            query: { status, origin },
        }),

    retryHypothesisJob: (jwt: string, jobId: number) =>
        request<HypothesisJobRow>(`/hypothesis/jobs/${jobId}/retry`, jwt, {
            method: 'POST',
        }),

    /** Let a research-only job form after all — the way out of RESEARCH_DONE. */
    formHypothesisJob: (jwt: string, jobId: number) =>
        request<{ queued: true }>(`/hypothesis/jobs/${jobId}/form`, jwt, {
            method: 'POST',
        }),

    triggerHypothesisForBrand: (
        jwt: string,
        brandId: number,
        options?: { sendTease?: boolean; mode?: 'full' | 'reform'; adminGuidance?: string },
    ) =>
        request<HypothesisJobRow>(`/hypothesis/brands/${brandId}/trigger`, jwt, {
            method: 'POST',
            body: {
                sendTease: options?.sendTease ?? false,
                mode: options?.mode ?? 'full',
                ...(options?.adminGuidance !== undefined
                    ? { adminGuidance: options.adminGuidance }
                    : {}),
            },
        }),

    // Phase 7 — subjects: platform brands and cold-outreach prospects
    listHypothesisSubjects: (jwt: string, type?: HypothesisSubjectType) =>
        request<HypothesisSubjectRow[]>('/hypothesis/subjects', jwt, {
            query: { type },
        }),

    createHypothesisProspect: (jwt: string, name: string, websiteUrl: string) =>
        request<HypothesisSubjectRow>('/hypothesis/subjects', jwt, {
            method: 'POST',
            body: { name, websiteUrl },
        }),

    linkHypothesisSubjectToCrm: (
        jwt: string,
        subjectId: number,
        crmCompanyId: number,
    ) =>
        request<HypothesisSubjectRow>(
            `/hypothesis/subjects/${subjectId}/link-crm`,
            jwt,
            { method: 'POST', body: { crmCompanyId } },
        ),

    deleteHypothesisSubject: (jwt: string, subjectId: number) =>
        request<{
            deleted: boolean;
            hypotheses: number;
            jobs: number;
            leadsParked: number;
        }>(
            `/hypothesis/subjects/${subjectId}`,
            jwt,
            { method: 'DELETE' },
        ),

    // Finish a run parked at AWAITING_REVIEWS using the review sources an
    // admin just supplied. Costs one synthesis: the site, the listings and the
    // competitors come from the stored snapshot rather than a second crawl.
    //
    // A refusal comes back as { resumed: false, reason }, not an error — the
    // job stays where it is so different sources can be tried.
    continueHypothesisWithReviews: (jwt: string, subjectId: number) =>
        request<{ resumed: boolean; reason?: string; sourcesFound?: number }>(
            `/hypothesis/subjects/${subjectId}/continue`,
            jwt,
            { method: 'POST' },
        ),

    triggerHypothesisForSubject: (
        jwt: string,
        subjectId: number,
        options?: { sendTease?: boolean; mode?: 'full' | 'reform'; adminGuidance?: string },
    ) =>
        request<HypothesisJobRow>(`/hypothesis/subjects/${subjectId}/trigger`, jwt, {
            method: 'POST',
            body: {
                sendTease: options?.sendTease ?? false,
                mode: options?.mode ?? 'full',
                ...(options?.adminGuidance !== undefined
                    ? { adminGuidance: options.adminGuidance }
                    : {}),
            },
        }),

    // Phase 6A — concierge runner panel (subject-keyed since Phase 7)
    getHypothesisSubjectDetail: (jwt: string, subjectId: number) =>
        request<HypothesisAdminDetail>(
            `/hypothesis/subjects/${subjectId}/detail`,
            jwt,
        ),

    getHypothesisTeasePreview: (jwt: string, subjectId: number) =>
        request<HypothesisTeasePreview>(
            `/hypothesis/subjects/${subjectId}/tease-preview`,
            jwt,
        ),

    sendHypothesisTease: (jwt: string, subjectId: number, resend?: boolean) =>
        request<{ sent: boolean }>(
            `/hypothesis/subjects/${subjectId}/send-tease`,
            jwt,
            { method: 'POST', body: { resend: resend === true } },
        ),

    getSubsystem: (jwt: string, id: SubsystemId) =>
        request<SubsystemDetail>(`/subsystems/${id}`, jwt),

    listVersions: (jwt: string, id: SubsystemId, limit?: number) =>
        request<SubsystemVersion[]>(`/subsystems/${id}/versions`, jwt, {
            query: { limit },
        }),

    listActivity: (jwt: string, id: SubsystemId, limit?: number) =>
        request<
            Array<{
                id: number;
                eventType: string;
                createdAt: string;
                userId: number | null;
                userEmail: string | null;
                entityType: string | null;
                entityId: number | null;
                metadata: Record<string, unknown> | null;
            }>
        >(`/subsystems/${id}/activity`, jwt, { query: { limit } }),

    getVersion: (jwt: string, id: SubsystemId, versionId: number) =>
        request<SubsystemVersion>(`/subsystems/${id}/versions/${versionId}`, jwt),

    diffVersions: (
        jwt: string,
        id: SubsystemId,
        from: number,
        to: number,
    ) =>
        request<SubsystemDiff>(`/subsystems/${id}/diff`, jwt, {
            query: { from, to },
        }),

    saveDraft: (
        jwt: string,
        id: SubsystemId,
        payload: unknown,
        notes?: string,
    ) =>
        request<SubsystemVersion>(`/subsystems/${id}/draft`, jwt, {
            method: 'POST',
            body: { payload, notes } as JsonShape,
        }),

    discardDraft: (jwt: string, id: SubsystemId) =>
        request<void>(`/subsystems/${id}/draft`, jwt, { method: 'DELETE' }),

    publishDraft: (jwt: string, id: SubsystemId, notes?: string) =>
        request<SubsystemVersion>(`/subsystems/${id}/publish`, jwt, {
            method: 'POST',
            body: { notes } as JsonShape,
        }),

    clearCache: (jwt: string, id: SubsystemId) =>
        request<{ rowsCleared: number; tables: string[] }>(
            `/subsystems/${id}/clear-cache`,
            jwt,
            { method: 'POST' },
        ),

    rollback: (
        jwt: string,
        id: SubsystemId,
        targetVersionId: number,
        notes?: string,
    ) =>
        request<SubsystemVersion>(
            `/subsystems/${id}/rollback/${targetVersionId}`,
            jwt,
            { method: 'POST', body: { notes } as JsonShape },
        ),

    // ----- Snapshot endpoints -----------------------------------------

    listSnapshots: (jwt: string) => request<GlobalSnapshot[]>('/snapshots', jwt),

    getSnapshot: (jwt: string, id: number) =>
        request<GlobalSnapshot>(`/snapshots/${id}`, jwt),

    createSnapshot: (jwt: string, name: string, notes?: string) =>
        request<GlobalSnapshot>('/snapshots', jwt, {
            method: 'POST',
            body: { name, notes } as JsonShape,
        }),

    restoreSnapshot: (jwt: string, id: number, notes?: string) =>
        request<{ snapshotId: number; perSubsystem: unknown[] }>(
            `/snapshots/${id}/restore`,
            jwt,
            { method: 'POST', body: { notes } as JsonShape },
        ),

    // ----- Shared prompt library --------------------------------------

    listSharedPromptLibrary: (jwt: string) =>
        request<SharedPromptLibraryEntry[]>('/shared-prompt-library', jwt),

    sharedPromptLibraryAvailableFor: (jwt: string, subsystem: SubsystemId) =>
        request<SharedPromptLibraryEntry[]>(
            `/shared-prompt-library/available-for/${subsystem}`,
            jwt,
        ),

    // ----- Sandbox scenarios + runs ----------------------------------

    listSandboxScenarios: (jwt: string, id: SubsystemId) =>
        request<SandboxScenario[]>(`/subsystems/${id}/sandbox/scenarios`, jwt),

    getSandboxScenario: (jwt: string, id: SubsystemId, scenarioId: number) =>
        request<SandboxScenarioDetail>(
            `/subsystems/${id}/sandbox/scenarios/${scenarioId}`,
            jwt,
        ),

    createSandboxScenario: (
        jwt: string,
        id: SubsystemId,
        body: {
            name: string;
            description?: string;
            brandId?: number | null;
            periodId?: number | null;
            inputsJson?: unknown | null;
            isCurated?: boolean;
        },
    ) =>
        request<SandboxScenarioDetail>(
            `/subsystems/${id}/sandbox/scenarios`,
            jwt,
            { method: 'POST', body: body as JsonShape },
        ),

    updateSandboxScenario: (
        jwt: string,
        id: SubsystemId,
        scenarioId: number,
        body: {
            name?: string;
            description?: string | null;
            brandId?: number | null;
            periodId?: number | null;
            inputsJson?: unknown | null;
            isCurated?: boolean;
        },
    ) =>
        request<SandboxScenarioDetail>(
            `/subsystems/${id}/sandbox/scenarios/${scenarioId}`,
            jwt,
            { method: 'PATCH', body: body as JsonShape },
        ),

    deleteSandboxScenario: (
        jwt: string,
        id: SubsystemId,
        scenarioId: number,
    ) =>
        request<void>(
            `/subsystems/${id}/sandbox/scenarios/${scenarioId}`,
            jwt,
            { method: 'DELETE' },
        ),

    estimateSandboxCost: (jwt: string, id: SubsystemId) =>
        request<{ averageCostUsd: number | null; sampleSize: number }>(
            `/subsystems/${id}/sandbox/estimate`,
            jwt,
        ),

    getSandboxSpendSummary: (jwt: string) =>
        request<{
            last24hUsd: number;
            todayUsd: number;
            perSubsystem24h: Array<{
                subsystem: string;
                usd: number;
                runs: number;
            }>;
        }>('/sandbox/spend', jwt),

    listSandboxExperts: (jwt: string, id: SubsystemId) =>
        request<{
            experts: Array<{ expertType: string; name: string }>;
        }>(`/subsystems/${id}/sandbox/experts`, jwt),

    listMeasuredCategoriesForBrand: (
        jwt: string,
        id: SubsystemId,
        brandId: number,
        period: string,
    ) =>
        request<{ categories: string[] }>(
            `/subsystems/${id}/sandbox/measured-categories`,
            jwt,
            { query: { brandId, period } },
        ),

    listSandboxRuns: (jwt: string, id: SubsystemId, limit?: number) =>
        request<SandboxRun[]>(`/subsystems/${id}/sandbox/runs`, jwt, {
            query: { limit },
        }),

    getSandboxRun: (jwt: string, id: SubsystemId, runId: number) =>
        request<SandboxRunDetail>(`/subsystems/${id}/sandbox/runs/${runId}`, jwt),

    createSandboxRun: (
        jwt: string,
        id: SubsystemId,
        body: {
            scenarioId?: number | null;
            brandId?: number | null;
            periodId?: number | null;
            inputs?: Record<string, unknown> | null;
            configAVersionId: number;
            configBVersionId: number;
        },
    ) =>
        request<SandboxRunDetail>(`/subsystems/${id}/sandbox/runs`, jwt, {
            method: 'POST',
            body: body as JsonShape,
        }),
};

// ----- Model connectivity test (lives under /admin/tools/ai-admin/) ----

export interface ModelTestResult {
    ok: boolean;
    kind: 'chat' | 'embedding';
    provider: string;
    modelId: string;
    latencyMs?: number;
    detail?: string;
    error?: string;
}

export async function testModelConnectivity(
    jwt: string,
    body: { kind: 'chat' | 'embedding'; provider: 'anthropic' | 'openai' | 'google'; modelId: string },
): Promise<ModelTestResult> {
    const url = `${apiBaseUrl}admin/tools/ai-admin/test-model`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${jwt}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        const errBody = await response.json().catch(() => null);
        throw new AiAdminApiError(
            response.status,
            errBody?.message || `Test failed (${response.status})`,
        );
    }
    return (await response.json()) as ModelTestResult;
}

export interface ModelBeliefsRunSummary {
    id: number;
    brandId: number | null;
    // The config the run was read from; null on runs from before configs.
    configId?: number | null;
    // The brand's name, or the ad-hoc target's.
    targetName?: string;
    adHoc?: boolean;
    status: string;
    stage: string | null;
    // True while an admin page read is working on a finished run.
    pageReading?: boolean;
    triggerSource: string;
    marketCountry: string | null;
    callsMade: number;
    callsBudget: number;
    // callsBudget governs STATEMENT calls only; the gate is planned separately
    // and recorded against the same run. Without the split, callsMade against
    // callsBudget reads as a budget overrun that never happened.
    callsByKind?: { statements: number; gate: number } | null;
    startedAt: string | null;
    completedAt: string | null;
    attemptCount: number;
    // Set while a run waits on a provider's daily quota. A parked run is not a
    // stalled one, and the table has to be able to say so.
    resumeAfter: string | null;
    note: string | null;
    lastError: string | null;
    // Whether the run was allowed to chase the interval target. Off is a valid
    // choice (cost certainty), but it has to be visible or a deliberately
    // imprecise read is indistinguishable from a broken one.
    adaptiveTopUp?: boolean;
    // How precise the run's per-statement estimates actually are.
    //
    // `unanimous` cells had every answer identical, so their interval is zero
    // wide — that is agreement, not precision, and counting it as "met" made
    // the old figure reward under-sampling. `share` is therefore over `varied`
    // cells only, and `medianHalfWidth` is the headline: it falls as samples
    // rise and cannot be gamed by sampling thinly.
    precision?: {
        met: number;
        varied: number;
        unanimous: number;
        cells: number;
        share: number | null;
        medianHalfWidth: number | null;
        target: number;
    } | null;
}

export interface ModelBeliefsRunsResponse {
    dailyCap: number;
    startedToday: number;
    capReached: boolean;
    nextResumeAt: string | null;
    runs: ModelBeliefsRunSummary[];
}

export interface ModelBeliefsRunDetail {
    runId: number;
    status: string;
    stage: string | null;
    callsMade: number;
    callsBudget: number;
    // callsBudget governs STATEMENT calls only; the gate is planned separately
    // and recorded against the same run. Without the split, callsMade against
    // callsBudget reads as a budget overrun that never happened.
    callsByKind: { statements: number; gate: number };
    startedAt: string | null;
    completedAt: string | null;
    lastError: string | null;
    samplesByStatus: Record<string, number>;
    answers: number;
    aggregates: number;
    results: number;
    // v4: what the code checks and the reviewer still found in the written
    // read after its last attempt. Null before a reviewed synthesis.
    synthesisIssues?: ModelBeliefsSynthesisIssues | null;
    panel?: { provider: string; model: string; label: string }[];
}

export interface ModelBeliefsSynthesisIssues {
    reviewed: boolean;
    retried: boolean;
    checks: { kind: string; match: string }[];
    review: {
        field: string;
        sentence: string;
        kind: string;
        statement: string;
        inputSays: string;
        fix: string;
    }[];
}

// v4: one page the rank question read, fetched and scored statement by statement.
export interface ModelBeliefsPageRow {
    id: number;
    url: string;
    domain: string;
    title: string | null;
    kind: string;
    listicle: boolean;
    answersJson: Record<string, number>;
    answersTotal: number;
    fetchVia: string | null;
    fetchStatus: number | null;
    chars: number;
    scoresJson: { code: string; covered: boolean; score: number | null; quote: string | null; quoteFound?: boolean }[] | null;
    readStatus: string;
    readModel: string | null;
    inputTokens: number;
    outputTokens: number;
    error: string | null;
}

export interface ModelBeliefsPagesResponse {
    runId: number;
    reading: boolean;
    settings: { enabled?: boolean; minAnswers?: number; maxChars?: number } | null;
    pages: ModelBeliefsPageRow[];
}

// v4: rank with a page read against rank without it, per model. Association,
// not cause; `clear` means the 90% interval excludes zero.
export interface ModelBeliefsPageEffect {
    feature: string;
    featureType: 'domain' | 'kind';
    kind: string;
    nPresent: number;
    nAbsent: number;
    scorePresent: number;
    scoreAbsent: number;
    diff: number;
    clear: boolean;
    listedPresent: number;
    listedAbsent: number;
}

export interface ModelBeliefsPageEffects {
    models: {
        provider: string;
        model: string;
        answers: number;
        outcomes: { name: string; isBrand: boolean; effects: ModelBeliefsPageEffect[] }[];
    }[];
}

export interface ModelBeliefsRunCost {
    runId: number;
    tokens: number;
    requests: number;
    searchRequests: number;
    tokenCostUsd: number;
    // Server-side web search is billed per request ON TOP of tokens, and does
    // not appear anywhere in token accounting. For a searching subsystem this
    // is the difference between a cost figure and a guess.
    searchCostUsd: number;
    totalCostUsd: number;
    byFeature: {
        featureType: string;
        tokens: number;
        requests: number;
        searchRequests: number;
    }[];
    byProvider: {
        provider: string;
        requests: number;
        tokens: number;
        searchRequests: number;
        // Total for the provider, tokens AND search, not the search share.
        costUsd: number;
    }[];
    byTarget: {
        target: string;
        targetKind: string;
        requests: number;
        tokens: number;
        searchRequests: number;
        costUsd: number;
    }[];
    byMode: {
        retrievalMode: string;
        requests: number;
        tokens: number;
        searchRequests: number;
        costUsd: number;
    }[];
    searchRate: {
        model: string;
        provider: string;
        asked: number;
        searched: number;
        rate: number;
    }[];
    costPerCall: {
        overall: number;
        byProvider: { provider: string; costPerCallUsd: number }[];
    };
    caveats: string[];
}

export interface ModelBeliefsGate {
    runId: number;
    summary: {
        byCell: {
            provider: string;
            model: string;
            retrievalMode: string;
            asked: number;
            included: number;
            inclusionRate: number;
            // Averaged over the answers that INCLUDED the brand only. A brand
            // listed once at rank 1 does not beat one listed every time at
            // rank 2, which is what inclusionRate is for.
            meanPosition: number | null;
        }[];
        byMode: {
            retrievalMode: string;
            asked: number;
            included: number;
            inclusionRate: number;
            meanPosition: number | null;
            modelsIncluding: number;
            modelsAsked: number;
        }[];
        competitors: {
            name: string;
            inclusionRate: number;
            meanPosition: number | null;
            // Verbatim. The reason a model gives for preferring a competitor
            // is the most directly actionable line in the whole read.
            reasons: string[];
        }[];
        unknownBrands: {
            name: string;
            count: number;
            inclusionRate?: number;
            meanPosition?: number | null;
            namedFirstRate?: number;
            rankScore?: number | null;
        }[];
    } | null;
}

export interface ModelBeliefsSources {
    runId: number;
    ledger: {
        entries: {
            domain: string;
            kind: string;
            count: number;
            statements: string[];
            titles: string[];
        }[];
        byKind: { kind: string; count: number; share: number }[];
        totalCitations: number;
        distinctDomains: number;
    } | null;
    impostorCandidates:
        | { domain: string; count: number; evidence: string[] }[]
        | null;
    pageEffects?: ModelBeliefsPageEffects | null;
}

export interface ModelBeliefsResultRow {
    target: string;
    provider: string;
    model: string | null;
    retrievalMode: string;
    view: string;
    brandEquity: number | null;
    answersUsed: number;
    coverage: Record<string, unknown> | null;
    elements: { element: string; score: number | null }[];
}

export interface ModelBeliefsSynthesis {
    headline: string;
    summary: string;
    gate_note: string;
    // v4: the pages that move the rank and what the most-read pages say.
    pages_note?: string | null;
    competitor_note: string;
    elements: {
        key: string;
        reading: string;
        disagreement: string | null;
        costing_recommendations: string | null;
    }[];
    record_fixes: {
        element: string;
        title: string;
        rationale: string;
        level: string;
        citations: string[];
    }[];
    takedowns: { domain: string; recommendation: string }[];
}

export interface ModelBeliefsPanel {
    members: {
        provider: string;
        model: string;
        label: string;
        batchSize?: number;
        nFloor?: number;
        readCompetitors?: boolean;
        inCatalogue: boolean;
        supportsWebSearch: boolean | null;
        webSearchTool: string | null;
        warnings: string[];
    }[];
    usable: number;
}

// The brand config (SYR-111 phase 2). Everything a read cannot invent: what a
// shopper calls the category, where the shopper is, which domains ARE the brand.
export interface ModelBeliefsBrandConfig {
    id: number;
    // Null for an ad-hoc target: a brand that is not on the platform.
    organisationId: number | null;
    brandId: number | null;
    // Ad-hoc targets only; a platform brand has these on its own record.
    name?: string | null;
    websiteUrl?: string | null;
    templateType?: string | null;
    consumerName: string;
    categoryLabel: string | null;
    marketCountry: string;
    marketCity: string | null;
    heroProductsJson: string[] | null;
    competitorNamesJson: string[] | null;
    brandDomainsJson: string[] | null;
    impostorDomainsJson: string[] | null;
    disambiguationNote: string | null;
    // Whether a high price is the brand's side of the Price statements. Null
    // until set; the read writes no record fix on price while it is null.
    pricePositioning: ModelBeliefsPricePositioning | null;
    // v4 phase 0: categories suggested from the brand's own site.
    categorySuggestionsJson?: ModelBeliefsCategorySuggestions | null;
    categorySuggestedAt?: string | null;
    createdAt?: string;
    updatedAt?: string;
}

export interface ModelBeliefsPricePositioningSuggestion {
    positioning: ModelBeliefsPricePositioning | 'unclear';
    // The site's own words that decided it.
    evidence: string;
    votes?: number;
    runs?: number;
}

export interface ModelBeliefsCategorySuggestions {
    // votes: how many of the suggestion runs named it (absent on a single run).
    suggestions: { phrase: string; covers: string; main: boolean; votes?: number }[];
    // Read from the same homepage call; a suggestion a person confirms.
    pricePositioning?: ModelBeliefsPricePositioningSuggestion | null;
    site: string;
    model: string | null;
    searches: number;
    suggestedAt: string;
    runs?: number;
}

export interface ModelBeliefsBrandRow {
    brandId: number;
    brandName: string;
    organisationId: number;
    organisationName: string | null;
    templateType: string | null;
    configured: boolean;
    consumerName: string | null;
    categoryLabel: string | null;
    marketCountry: string | null;
    marketCity: string | null;
    competitorCount: number;
    brandDomainCount: number;
    // A config without a category label cannot be read at all: the gate has
    // nothing to ask about.
    readable: boolean;
    lastRun: {
        id: number;
        status: string;
        completedAt: string | null;
        createdAt: string;
    } | null;
}

export type ModelBeliefsGroup = 'A' | 'B' | 'C';

export interface ModelBeliefsStatement {
    code: string;
    category: string;
    voice: 'subject' | 'witness' | 'fact';
    noReferent: boolean;
    // Adir's v3 group: A facts and B opinions are asked, C feelings are the
    // survey's. null means unseeded and never asked.
    group: ModelBeliefsGroup | null;
    // The self view comes from the rank question, not from the statement.
    selfFromRank: boolean;
    allowsNa: boolean;
    scale: string;
    wordings: { id: string; text: string }[];
    // Beyond w0. Zero is the policy since the wording probe: rotating a fact
    // item averages questions of different claim strength.
    paraphrases: number;
}

export interface ModelBeliefsStatementsResponse {
    templateType: string;
    statements: ModelBeliefsStatement[];
    withoutParaphrases: number;
    byGroup: { A: number; B: number; C: number; unseeded: number };
}

export interface ModelBeliefsBrandHistory {
    brandId: number;
    brandName: string;
    points: {
        date: string;
        aiBrandEquity: number | null;
        gateInclusionRate: number | null; // fraction 0..1, as the gate reports it
        // What produced the point. A read taken with fewer models than the
        // panel is not the same instrument as a full one, and the difference
        // between them would otherwise read as movement.
        models: number;
        panelSize: number;
    }[];
    refreshDays: number;
}

export interface ModelBeliefsBrandsResponse {
    brands: ModelBeliefsBrandRow[];
    total: number;
    configured: number;
}

// What previous runs discovered and nobody has written down: brands the gate
// listed that are not configured competitors, and domains carrying the brand's
// name that are not known to be the brand — which, until a domain is
// configured, includes the brand's own site.
export interface ModelBeliefsConfigSuggestions {
    fromRunId: number | null;
    competitors: { name: string; count: number }[];
    domains: { domain: string; count: number; evidence: string[] }[];
}

export interface ModelBeliefsCompetitorSuggestions {
    suggestions: { name: string; why: string; votes?: number }[];
    site: string;
    category: string;
    model: string | null;
    searches: number;
    suggestedAt: string;
    runs?: number;
}

// The admin form for a brand that is not on the platform. Mirrors the brand
// config: the category is what the gate asks about and must not name the
// brand; the country is the market; the site gives the brand's own domain.
export type ModelBeliefsPricePositioning = 'premium' | 'value';

export interface ModelBeliefsAdhocTarget {
    name: string;
    pricePositioning?: ModelBeliefsPricePositioning | null;
    consumerName?: string;
    websiteUrl: string;
    category: string;
    country: string;
    city?: string;
    competitorNames: string[];
    disambiguation?: string;
}

export interface ModelBeliefsTriggerOverrides {
    dryRun?: boolean;
    triggerSource?: 'brand' | 'admin';
    country?: string;
    // Ad-hoc targets only.
    templateType?: string;
    // Ad-hoc targets only: read this saved target again instead of saving the
    // form as a new one.
    configId?: number;
    note?: string;
}

// The dry run's plan, or the queued run. `dryRun` tells them apart.
export interface ModelBeliefsTriggerResponse {
    dryRun?: boolean;
    runId?: number;
    // The config the run reads from (an ad-hoc start saves one).
    configId?: number | null;
    status?: string;
    startsWithin?: string;
    callsBudget: number;
    statementCalls?: number;
    gateCalls?: number;
    maxCallsWithTopUp?: number;
    budgetScaled: boolean;
    scaleFactor: number;
    cells: number;
    callsByModel?: Record<string, number>;
    panel?: { provider: string; model: string; label?: string; batchSize?: number; readsPages?: boolean }[];
    targets?: string[];
    competitorsDropped?: string[] | null;
    competitorNote?: string | null;
    statements?: number;
    groupsAsked?: string[];
    statementsNotAsked?: number;
    gateBattery?: { prompts: number; personas: number; gateN: number };
    settings?: Record<string, unknown>;
    pageRead?: {
        enabled: boolean;
        minAnswers?: number;
        maxChars?: number;
        reader?: { provider: string; model: string; label?: string } | null;
    };
    country?: string;
    snapshot?: {
        consumerName?: string;
        country?: string;
        templateType?: string;
        competitorNames?: string[];
        statements?: number;
    };
}

// An ad-hoc target in the Brands tab's "not on the platform" list.
export interface ModelBeliefsAdhocConfigRow extends ModelBeliefsBrandConfig {
    lastRun: { id: number; status: string; createdAt: string; completedAt: string | null } | null;
}

export interface ModelBeliefsBrandConfigResponse {
    // Null for an ad-hoc target; then configId carries the row.
    brandId: number | null;
    configId?: number;
    brand?: {
        id: number | null;
        name: string;
        organisationId: number | null;
        templateType: string | null;
    };
    config: ModelBeliefsBrandConfig | null;
    markets?: { country: string; surveys: number }[];
    // What a run will actually read: the planner takes the first
    // `maxCompetitors` names and silently ignores the rest, and the whole
    // competitor leg can be switched off platform-wide.
    limits?: { maxCompetitors: number; readCompetitors: boolean };
    warnings: string[];
    suggestions: ModelBeliefsConfigSuggestions;
}

// Only the editable fields, and presence decides: a key sent as null CLEARS
// the stored value, a key left out keeps it.
export interface ModelBeliefsBrandConfigPatch {
    consumerName?: string | null;
    categoryLabel?: string | null;
    marketCountry?: string | null;
    marketCity?: string | null;
    heroProductsJson?: string[] | null;
    competitorNamesJson?: string[] | null;
    brandDomainsJson?: string[] | null;
    impostorDomainsJson?: string[] | null;
    disambiguationNote?: string | null;
    pricePositioning?: ModelBeliefsPricePositioning | null;
    // Ad-hoc targets only.
    name?: string | null;
    websiteUrl?: string | null;
    templateType?: string | null;
}

export { AiAdminApiError };
